
import {
  Vue,
  Component,
  Prop,
  Ref,
  Model,
  Watch,
} from "vue-property-decorator";
import {
  ElementUploadFile,
  ElUpload,
  ImageInfo,
  UploadResponse,
} from "./definitions";
import { preview } from "@/utils/image";
import { getImageBlobData, downloadBlob, download } from "@/utils";
import ImageCrop from "@/components/image-crop/index.vue";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import { getFileSizeWithUnit } from "./utils";
import { Message } from "element-ui";
import { getImagesServer } from "@/service/ewold/api/newProduct";
import { ImageServer } from "@/service/ewold/entity/ImageServer";
const showFileTypeErrMsg = debounce(() => {
  Message.error("请选择图片");
}, 300);
function showSizeExceedErrMsg(size: number) {
  return throttle(() => {
    Message.error(`图片大小不能大于${getFileSizeWithUnit(size)}`);
  }, 300);
}
@Component({
  components: {
    ImageCrop,
  },
})
export default class ImageUploader extends Vue {
  /** 绑定值 */
  @Model("change", { type: Array, required: false }) readonly value?: string[];
  /** 是否自动上传 */
  @Prop({ required: false, default: false }) autoUpload?: boolean;
  /** 是否支持多选图片 */
  @Prop({ required: false, default: false }) multiple?: boolean;
  /** 接收类型 */
  @Prop({ required: false, default: "image/*" }) accept?: string;
  /** 是否支持下载 */
  @Prop({ required: false, default: false }) dowloadable?: boolean;
  /** 是否支持裁剪 */
  @Prop({ required: false, default: false }) cropable?: boolean;
  /** 裁剪比例固定 */
  @Prop({ required: false, default: false }) cropRatioFixed?: boolean;
  /** 最大允许上传个数 */
  @Prop({ required: false }) limit?: number;
  /** 文件超出个数限制时的钩子 */
  @Prop({ required: false }) onExceed?: (
    files: File[],
    fileList: ElementUploadFile<UploadResponse>[]
  ) => void;
  /** 是否禁用 */
  @Prop({ required: false, default: false }) disabled?: boolean;
  /** 覆盖默认的上传行为，可以自定义上传的实现 */
  @Prop({ required: false }) httpRequest?: () => void;
  /** 单个文件大小限制(单位 byte)*/
  @Prop({ required: false }) maxSize?: number;
  @Ref("uploader") elUploader!: ElUpload<UploadResponse>;
  fileList: {
    name?: string;
    url: string;
    status: string;
    response?: UploadResponse;
  }[] = [];
  dialogImageUrl = "";
  dialogVisible = false;
  imageCropState = {
    image: "",
  };
  imageServerInfo?: ImageServer;
  uploadUrl = "";
  imageServerInfoLastFetch?: Date;
  uploadData: { savepath: string } = { savepath: "" };
  latestValue: string[] = []; //最后一次更新的值
  uploadSuccessCallback?: (success: boolean) => void;
  get showSizeExceedErrMsgFn() {
    if (typeof this.maxSize === "number") {
      return showSizeExceedErrMsg(this.maxSize);
    }
    return null;
  }
  @Watch("value", { immediate: true, deep: true })
  handleValueChange(val: string[]) {
    if (JSON.stringify(val) === JSON.stringify(this.latestValue)) {
      return;
    }
    console.log("handleValueChange", val);
    if (Array.isArray(val)) {
      this.fileList = val.map((ele) => ({
        name: "",
        percentage: 100,
        status: "success",
        url: ele,
        needUpload: false,
      }));
    }
  }
  mounted() {
    this.getImagesServerInfo();
  }
  async getImagesServerInfo() {
    this.imageServerInfoLastFetch = new Date();
    const res = await getImagesServer();
    if (res && res.result) {
      this.imageServerInfo = res.result;
      this.uploadUrl = `${this.imageServerInfo.upload_url}?perm_code=${this.imageServerInfo.perm_code}`;
      return res.result;
    }
  }
  progressStatusFormat(file: ElementUploadFile<UploadResponse>) {
    const remoteUrl = this.getRemoteImageUrl(file);
    let status = "";
    switch (file.status) {
      case "success":
        if (remoteUrl) {
          status = `success`;
        } else {
          status = "exception";
        }
        break;
      case "error":
        status = `exception`;
        break;
    }
    return status;
  }
  progressTextFormat(
    percentage: number,
    file: ElementUploadFile<UploadResponse>
  ) {
    let text = "";
    switch (file.status) {
      case "ready":
        break;
      case "uploading":
        text = `${percentage.toFixed(2)}%`;
        break;
      case "error":
        text = `上传失败`;
    }
    return text;
  }
  isUploadSuccess(file: ElementUploadFile<UploadResponse>) {
    const remoteUrl = this.getRemoteImageUrl(file);
    return !!remoteUrl;
  }
  getRemoteImageUrl(file: ElementUploadFile<UploadResponse>) {
    if (file.needUpload === false && file.url) {
      return file.url;
    }
    const res = file.response;
    if (Array.isArray(res?.data) && res?.data.length) {
      const path = res.data[0].Url;
      return path
        ? `https://img.sellercube.com/${path.replace(/^\/+/, "")}`
        : "";
    }
    return "";
  }
  async handleRemove(file: ElementUploadFile<UploadResponse>) {
    const index = this.elUploader.uploadFiles.findIndex(
      (ele) => ele.uid === file.uid
    );
    if (index > -1) {
      this.remove(index);
    }
  }
  remove(index: number) {
    this.elUploader.uploadFiles.splice(index, 1);
    this.triggerChange();
  }
  handlePictureCardPreview(file: ElementUploadFile<UploadResponse>) {
    preview({
      url: file.url,
    });
  }
  async handleDownload(file: ElementUploadFile<UploadResponse>) {
    if (!file.url) {
      return;
    }
    if (file.url.startsWith("blob:")) {
      download(file.url, file.name);
    } else {
      const imgBlob = await getImageBlobData(file.url);
      downloadBlob(imgBlob, file.name);
    }
  }
  handleCrop(file: ElementUploadFile<UploadResponse>) {
    this.imageCropState.image = file.url;
  }
  onChange(
    file: ElementUploadFile<UploadResponse>,
    fileList: ElementUploadFile<UploadResponse>[]
  ) {
    console.log("onChange file", file);
    const isImg = file?.raw.type.startsWith("image/");
    const removeFile = () => {
      const index = fileList.findIndex((f) => f.uid === file.uid);
      fileList.splice(index, 1);
    };
    if (!isImg) {
      showFileTypeErrMsg();
      removeFile();
    } else if (
      typeof this.maxSize === "number" &&
      file?.raw.size > this.maxSize &&
      typeof this.showSizeExceedErrMsgFn === "function" &&
      getFileSizeWithUnit(file?.raw.size) !== getFileSizeWithUnit(this.maxSize)
    ) {
      this.showSizeExceedErrMsgFn();
      removeFile();
    }
    if (this.autoUpload) {
      this.upload();
    }
    this.triggerChange();
  }
  onSuccess() {
    if (
      this.isUploadSuccessAll() &&
      typeof this.uploadSuccessCallback === "function"
    ) {
      this.uploadSuccessCallback(true);
    }
  }
  triggerChange() {
    const value = this.getUploadFilesUrl();
    this.latestValue = [...value];
    this.$emit("change", [...value]);
  }
  getUploadFilesUrl(): string[] {
    return this.elUploader.uploadFiles.map((ele) => {
      return this.getRemoteImageUrl(ele) || ele.url;
    });
  }
  /**
   * 获取图片
   */
  async getImagesInfo(): Promise<ImageInfo[]> {
    const uploadFiles = this.elUploader.uploadFiles;
    const promises = uploadFiles.map((uploadFile, index) => {
      return new Promise((resplve: (imgInfo: ImageInfo) => void, reject) => {
        const img = new Image();
        img.src = uploadFile.url;
        img.onload = () => {
          const info: ImageInfo = {
            index,
            file: uploadFile.raw,
            el: img,
            url: uploadFile.url,
            status: uploadFile.status,
          };
          resplve(info);
        };
        img.onerror = (err) => {
          reject(err);
        };
      });
    });
    const res = await Promise.all(promises).catch(() => []);
    return res;
  }
  checkImageServerInfoFresh() {
    const time = Date.now();
    const fetchedDate = this.imageServerInfoLastFetch as Date;
    const expire = 1 * 60 * 60 * 1000; //1小时过期
    return time - fetchedDate.getTime() < expire;
  }
  async beforeUpload(file: ElementUploadFile<UploadResponse>) {
    console.log(file);
    const isServerInfoExpire = !this.checkImageServerInfoFresh();
    if (isServerInfoExpire) {
      const res = await this.getImagesServerInfo().catch(() => false);
      if (!res) {
        return false;
      }
    }
    this.uploadData.savepath = file.name;
    return true;
  }
  async upload(): Promise<ElementUploadFile<UploadResponse>[] | null> {
    if (this.isUploadSuccessAll()) {
      return this.elUploader.uploadFiles;
    }
    if (!this.uploadUrl) {
      this.$message.warning("上传组件未就绪，请稍后重试");
      return null;
    }
    this.elUploader.submit();
    return new Promise((resolve, reject) => {
      this.uploadSuccessCallback = (success: boolean) => {
        if (success) {
          resolve(this.elUploader.uploadFiles);
        } else {
          reject(this.elUploader.uploadFiles);
        }
      };
    });
  }
  isUploadSuccessAll() {
    return this.elUploader.uploadFiles.every(
      (uf) => uf.status === "success" && !!this.getRemoteImageUrl(uf)
    );
  }
}
